import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Calendar, ClipboardCheck, FileText, CreditCard, 
  Users, MapPin, Plus, X, Cog, Package2, CheckSquare, Clock, DollarSign,
  Minus, Check, MessageCircle, Wrench, Activity, BarChart, ListChecks, Building2, MessageSquare, Car, Megaphone, Calculator
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { Resource, getResources } from '../lib/resources';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import ActiveTimerWarningModal from './ActiveTimerWarningModal';

// Add animation keyframes
const customStyles = `
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-slideInRight {
    animation: slideInRight 0.3s ease-out forwards;
  }

  .animate-slideInUp {
    animation: slideInUp 0.3s ease-out forwards;
  }

  /* Basic styles for the bottom nav container */
  .mobile-nav-container {
    /* REMOVED: All visual styling that creates translucent elements */
    /* No positioning, no background, no borders, no shadows, no backdrop filters */
    /* Keep minimal structure for React component but make it completely invisible */
    position: relative;
    width: 100%;
    height: 0; /* No height so it takes up no visual space */
    z-index: 1; /* Lower z-index so it doesn't interfere */
    /* Make container completely transparent and invisible */
    background: none !important;
    border: none !important;
    box-shadow: none !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  /* iOS safe area padding - REMOVED for browser mode */
  .mobile-nav-container {
    /* REMOVED: All padding that could create visual space */
    padding: 0 !important;
    margin: 0 !important;
    pointer-events: none; /* Allow scrolling through the nav container */
  }
  
  /* But make sure FAB button can be clicked */
  .mobile-nav-container .fab-container,
  .mobile-nav-container .fab-container * {
    pointer-events: auto;
  }

  /* Ensure FAB menu items are clickable */
  .mobile-nav-container .fab-menu-items,
  .mobile-nav-container .fab-menu-items *,
  .mobile-nav-container .fab-menu-items button {
    pointer-events: auto !important;
    z-index: 1200 !important;
  }

  /* Special styles for PWA mode - REMOVE ALL VISUAL ELEMENTS */
  .pwa-mode .mobile-nav-container {
    /* REMOVED: All PWA-specific visual styling */
    position: relative !important; /* Changed from fixed */
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    width: auto !important;
    height: 0 !important; /* No height */
    z-index: 1 !important; /* Lower z-index */
    margin: 0 !important;
    padding: 0 !important; /* No padding */
    background: none !important; /* No background */
    border: none !important; /* No borders */
    box-shadow: none !important; /* No shadows */
    backdrop-filter: none !important; /* No backdrop filter */
    -webkit-backdrop-filter: none !important; /* No webkit backdrop filter */
  }

  /* Active item styling */
  .nav-item {
    position: relative;
    transition: all 0.2s ease;
    padding-top: 8px !important;
    padding-bottom: 2px !important;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;  /* Take full width of allocated space */
  }
  
  .nav-item.active {
    color: #3b82f6;
  }
  
  /* Blue dot indicator removed as requested */
  
  /* Position nav icons higher and make larger */
  .nav-item svg {
    margin-bottom: 4px;
    height: 22px !important;
    width: 22px !important;
  }
  
  /* Adjust label positioning */
  .nav-item span {
    font-size: 0.75rem;
    margin-top: 0 !important;
    font-weight: 500;
  }
  
  /* Position FAB at the far right */
  .fab-container {
    position: fixed;
    right: 16px;
    bottom: 100px; /* Increased from 80px to move it higher */
    z-index: 85; /* Position above sidebar (80) but below header (90) */
  }

  /* FAB Button enhancement */
  .nav-fab {
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3) !important;
    position: relative;
    z-index: 85 !important; /* Match container z-index */
    transform: translateY(0);
    background: linear-gradient(to right, #3b82f6, #4f46e5) !important;
    height: 56px !important;
    width: 56px !important;
  }

  .nav-fab:active {
    transform: translateY(0px) scale(0.92);
  }

  /* Create space for the FAB button */
  .nav-content-wrapper {
    position: relative;
    padding: 6px 2px 14px 2px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  /* Badge styles */
  .badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background-color: #ef4444;
    color: white;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }
`;

interface NavigationItem {
  path: string;
  icon: React.ReactNode;
  label: string;
}

interface MobileBottomNavigationProps {
  hasCalendar: boolean;
  hasBookings: boolean;
  proposalsAccess: "manage" | "view" | "disabled";
  hasPayments: boolean;
  hasCustomers: boolean;
  // FAB related props
  permissionLevel?: "manage" | "view" | "disabled";
  onSuccess?: () => void;
  // Functions to open/close modals
  openCreateBookingModal?: () => void;
  openCreateMobileBookingModal?: () => void;
  openCreateProposalModal?: () => void;
  openPaymentModal?: () => void;
  openWithdrawStockModal?: () => void;
  // Additional props for checking permissions
  hasSettings?: boolean;
  hasMessages?: boolean;
  // Callback for navigation item changes
  onNavItemsChange?: (items: string[]) => void;
}

export default function MobileBottomNavigation({
  hasCalendar,
  hasBookings,
  proposalsAccess,
  hasPayments,
  hasCustomers,
  permissionLevel,
  onSuccess,
  openCreateBookingModal,
  openCreateMobileBookingModal,
  openCreateProposalModal,
  openPaymentModal,
  openWithdrawStockModal,
  hasSettings = true,
  hasMessages = false,
  onNavItemsChange
}: MobileBottomNavigationProps) {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();
  const location = useLocation();
  const navigate = useNavigate();
  const [showBookingOptions, setShowBookingOptions] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const { unreadCount } = useUnreadMessages();
  
  // Timeclock state
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [currentPayrollEntryId, setCurrentPayrollEntryId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [compensationType, setCompensationType] = useState<'hourly' | 'flat_rate' | 'commission' | null>(null);
  const [showClockOutError, setShowClockOutError] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);
  
  // Active timer warning modal state
  const [isActiveTimerWarningOpen, setIsActiveTimerWarningOpen] = useState(false);
  const [activeTimerBookingTitle, setActiveTimerBookingTitle] = useState<string>('');
  
  // Navigation items - customizable navigation config
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
  const [availableNavItems, setAvailableNavItems] = useState<NavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [firstRender, setFirstRender] = useState(true);

  // PWA detection state
  const [isPWA, setIsPWA] = useState(false);
  
  // Pre-initialize navigation with safe values to prevent flashing
  useEffect(() => {
    // Safe default navigation items that never include sales reports
    const safeDefault = [
      {
        path: '/pipeline',
        icon: <MessageSquare className="h-5 w-5" />,
        label: 'Pipeline'
      },
      {
        path: '/calendarV2',
        icon: <Calendar className="h-5 w-5" />,
        label: 'Calendar'
      },
      {
        path: '/bookings',
        icon: <ClipboardCheck className="h-5 w-5" />,
        label: 'Bookings'
      },
      {
        path: '/payments',
        icon: <CreditCard className="h-5 w-5" />,
        label: 'Payments'
      }
    ];
    
    // Apply safe defaults immediately
    setNavigationItems(safeDefault);
    
    // After a small timeout, allow rendering
    setTimeout(() => {
      setFirstRender(false);
    }, 100);
  }, []);
  
  // Check if app is running in PWA mode
  useEffect(() => {
    // Method 1: Check for display-mode: standalone
    const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 2: Check for iOS PWA (using window.navigator as any to bypass TypeScript error)
    const isIOSPWA = 
      // @ts-ignore - 'standalone' exists on iOS Safari but not in the TypeScript definition
      (window.navigator as any).standalone || 
      window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 3: Check for PWA launch parameters
    const urlParams = new URLSearchParams(window.location.search);
    const launchedAsPWA = urlParams.get('pwa') === 'true';
    
    setIsPWA(isDisplayModeStandalone || isIOSPWA || launchedAsPWA);
    
    // Log PWA status for debugging
    console.log('PWA detection:', { 
      isDisplayModeStandalone, 
      isIOSPWA, 
      launchedAsPWA,
      isPWA: isDisplayModeStandalone || isIOSPWA || launchedAsPWA
    });
    
    // Add PWA class to body if in PWA mode
    if (isDisplayModeStandalone || isIOSPWA || launchedAsPWA) {
      document.body.classList.add('pwa-mode');
    } else {
      document.body.classList.remove('pwa-mode');
    }
  }, []);

  // Get all available navigation items
  const getAllNavigationItems = useCallback(() => {
    const items: NavigationItem[] = [];
    let salesReportsItem: NavigationItem | null = null;
    
    // Remove the Home option
    // No longer adding Home by default
    
    // Main navigation items
    if (hasCalendar && canAccess.calendar) {
      items.push({
        path: '/calendar',
        icon: <Calendar className="h-5 w-5" />,
        label: 'Calendar'
      });
      
      // Add Location Calendar item right after regular Calendar
      items.push({
        path: '/calendarV2',
        icon: <Calendar className="h-5 w-5" />,
        label: 'Location'
      });
      
      // Add Mobile Calendar item
      items.push({
        path: '/mobile-calendar',
        icon: <Calendar className="h-5 w-5" />,
        label: 'Mobile'
      });
    }
    
    if (hasBookings && canAccess.bookings) {
      items.push({
        path: '/bookings',
        icon: <ClipboardCheck className="h-5 w-5" />,
        label: 'Bookings'
      });
    }
    
    if (proposalsAccess !== 'disabled' && canAccess.proposals) {
      items.push({
        path: '/proposals',
        icon: <FileText className="h-5 w-5" />,
        label: 'Proposals'
      });
    }
    
    if (hasPayments) {
      items.push({
        path: '/payments',
        icon: <CreditCard className="h-5 w-5" />,
        label: 'Payments'
      });
    }
    
    if (hasCustomers && canAccess.customers) {
      items.push({
        path: '/customers',
        icon: <Users className="h-5 w-5" />,
        label: 'Customers'
      });
    }
    
    // Always include GPS
    // Temporarily hidden
    /*
    items.push({
      path: '/gps',
      icon: <MapPin className="h-5 w-5" />,
      label: 'GPS'
    });
    */
    
    // Tasks - check if disabled in settings and has permission
    if (!isPageDisabled('tasks') && canAccess.tasks) {
      items.push({
        path: '/tasks',
        icon: <ListChecks className="h-5 w-5" />,
        label: 'Tasks'
      });
    }
    
    // Add automations if not disabled in settings and user has permission
    if (!isPageDisabled('automations') && canAccess.automations) {
      items.push({
        path: '/automations',
        icon: <Activity className="h-5 w-5" />,
        label: 'Automations'
      });
    }
    
    // Communications - only include if user has messages permission and not disabled in settings
    if (!isPageDisabled('messages') && canAccess.messages) {
      console.log('MobileBottomNavigation: Adding Pipeline to navigation items');
      
      // Add Pipeline page - remove the Communications/Messages entry
      items.push({
        path: '/pipeline',
        icon: <MessageSquare className="h-5 w-5" />,
        label: 'Pipeline'
      });
    } else {
      console.log('MobileBottomNavigation: Messages excluded from navigation items due to permissions');
    }
    
    // Add other navigation items from left nav that aren't already included
    
    // Marketing link - check if disabled in settings and has permission
    if (!isPageDisabled('marketing') && canAccess.marketing) {
      items.push({
        path: '/marketing',
        icon: <Megaphone className="h-5 w-5" />,
        label: 'Marketing'
      });
    }
    
    // Time / Accounting link - check permission
    if (canAccess.timeAccounting) {
      items.push({
        path: '/time-accounting',
        icon: <Calculator className="h-5 w-5" />,
        label: 'Time / Accounting'
      });
    }
    
    // Stock/Supplies link - check if disabled in app settings and has permission
    if (!isPageDisabled('supplies') && canAccess.supplies) {
      items.push({
        path: '/stock',
        icon: <Package2 className="h-5 w-5" />,
        label: 'Supplies'
      });
    }
    
    // Resources link - check if disabled in app settings and has permission
    if (!isPageDisabled('resources') && canAccess.resources) {
      items.push({
        path: '/resources',
        icon: <Users className="h-5 w-5" />,
        label: 'Resources'
      });
    }
    
    // Services link - check if disabled in app settings and has permission
    if (!isPageDisabled('services') && canAccess.services) {
      items.push({
        path: '/services',
        icon: <Wrench className="h-5 w-5" />,
        label: 'Services'
      });
    }
    
    // Sales Reports link - Store separately to add at the end
    if (!isPageDisabled('sales-reports')) {
      salesReportsItem = {
        path: '/sales-reports',
        icon: <BarChart className="h-5 w-5" />,
        label: 'Sales Reports'
      };
    }
    
    // Settings link
    if (hasSettings) {
      items.push({
        path: '/settings',
        icon: <Cog className="h-5 w-5" />,
        label: 'Settings'
      });
    }
    
    // Add salesReportsItem at the end to ensure it's not in the first few items
    if (salesReportsItem) {
      items.push(salesReportsItem);
    }
    
    return items;
  }, [hasCalendar, hasBookings, proposalsAccess, hasPayments, hasCustomers, hasMessages, 
      hasSettings]);

  // Load navigation configuration from database
  const loadNavConfigFromDatabase = async () => {
    if (!supabase || !user?.id) {
      console.log('Cannot load nav config from database: Missing supabase client or user ID');
      return null;
    }
    
    try {
      console.log('Loading navigation config from database for user:', user.id);
      
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preference_value, updated_at')
        .eq('user_id', user.id)
        .eq('preference_type', 'mobile_navigation')
        .maybeSingle();
        
      if (error) {
        console.error('Error loading navigation config from database:', error);
        return null;
      }
      
      if (data?.preference_value) {
        console.log('Loaded navigation config from database:', data.preference_value, 'updated at:', data.updated_at);
        return {
          config: data.preference_value,
          updatedAt: new Date(data.updated_at)
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error loading navigation config from database:', error);
      return null;
    }
  };

  // Define all navigation items
  useEffect(() => {
    console.log('MobileBottomNavigation: Permission check - Messages access:', hasMessages);
    
    // Filter function to ensure sales reports is never included initially
    const preventSalesReports = (items: NavigationItem[]): NavigationItem[] => {
      return items.filter(item => item.path !== '/sales-reports');
    };
    
    const items = preventSalesReports(getAllNavigationItems());
    setIsLoading(true);
    setLoadError(null);
    
    // Load configuration from database only
    const loadConfiguration = async () => {
      try {
        setIsLoading(true);
        
        // Add a small delay to ensure proper loading
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if we have the required dependencies
        if (!supabase || !user?.id) {
          console.log('MobileBottomNavigation: Waiting for Supabase client or user ID...');
          
          // Use default navigation items when dependencies aren't ready
          const preferredItems = ['/calendarV2', '/bookings', '/proposals', '/customers'];
          const defaultItems = items
            .filter(item => preferredItems.includes(item.path));
          
          if (defaultItems.length < 4) {
            const additionalItems = items
              .filter(item => 
                !defaultItems.some(di => di.path === item.path) &&
                item.path !== '/sales-reports'
              )
              .slice(0, 4 - defaultItems.length);
            
            defaultItems.push(...additionalItems);
          }
          
          console.log('MobileBottomNavigation: Using default items while waiting for dependencies:', defaultItems.map(i => i.path));
          setNavigationItems(defaultItems);
          setAvailableNavItems(items.filter(item => !defaultItems.some(di => di.path === item.path)));
          
          if (onNavItemsChange) {
            onNavItemsChange(defaultItems.map(item => item.path));
          }
          
          return;
        }
        
        // Additional check to ensure user is properly loaded
        if (!user.id || user.id === '') {
          console.log('MobileBottomNavigation: User ID not ready yet, using default items');
          
          const preferredItems = ['/calendarV2', '/bookings', '/proposals', '/customers'];
          const defaultItems = items
            .filter(item => preferredItems.includes(item.path));
          
          if (defaultItems.length < 4) {
            const additionalItems = items
              .filter(item => 
                !defaultItems.some(di => di.path === item.path) &&
                item.path !== '/sales-reports'
              )
              .slice(0, 4 - defaultItems.length);
            
            defaultItems.push(...additionalItems);
          }
          
          setNavigationItems(defaultItems);
          setAvailableNavItems(items.filter(item => !defaultItems.some(di => di.path === item.path)));
          
          if (onNavItemsChange) {
            onNavItemsChange(defaultItems.map(item => item.path));
          }
          
          return;
        }
        
        // Load from database
        const dbConfigResult = await loadNavConfigFromDatabase();
        
        if (dbConfigResult?.config) {
          console.log('MobileBottomNavigation: Loaded paths from database:', dbConfigResult.config);
          processNavConfig(dbConfigResult.config, items);
          return;
        }
        
        // If no database config exists, use default and save to database
        console.log('MobileBottomNavigation: No saved config found, using default');
        
        // Create a specific default navigation (never include sales-reports)
        const preferredItems = ['/calendarV2', '/bookings', '/proposals', '/customers'];
        
        // Find existing items that match our preferred paths, excluding sales-reports
        const defaultItems = items
          .filter(item => preferredItems.includes(item.path));
        
        // If we don't have enough items, add others (except sales-reports)
        if (defaultItems.length < 4) {
          const additionalItems = items
            .filter(item => 
              !defaultItems.some(di => di.path === item.path) && // Not already included
              item.path !== '/sales-reports' // Never include sales-reports
            )
            .slice(0, 4 - defaultItems.length);
          
          defaultItems.push(...additionalItems);
        }
        
        console.log('MobileBottomNavigation: Default items', defaultItems.map(i => i.path));
        
        setNavigationItems(defaultItems);
        setAvailableNavItems(items.filter(item => !defaultItems.some(di => di.path === item.path)));
        
        // Save default config to database (only if we have the required dependencies)
        if (supabase && user?.id) {
          const defaultPaths = defaultItems.map(item => item.path);
          saveNavConfigToDatabase(defaultPaths);
        }
        
        // Notify parent about items
        if (onNavItemsChange) {
          onNavItemsChange(defaultItems.map(item => item.path));
        }
      } catch (error) {
        console.error('Error loading saved navigation:', error);
        setLoadError('Failed to load navigation settings');
        
        // Defensive fallback - ensure no sales-reports in default
        const safeItems = items
          .filter(item => item.path !== '/sales-reports')
          .slice(0, 4);
        
        setNavigationItems(safeItems);
        setAvailableNavItems(items.filter(item => !safeItems.some(si => si.path === item.path)));
        
        // Notify parent about items
        if (onNavItemsChange) {
          onNavItemsChange(safeItems.map(item => item.path));
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Process navigation configuration with permission checks
    const processNavConfig = (savedPaths: string[], allItems: NavigationItem[]) => {
      // Remove sales-reports from paths regardless of what's saved in the database
      savedPaths = savedPaths.filter(path => path !== '/sales-reports');
      
      // Check if any saved path is no longer allowed due to permissions
      const filteredSavedPaths = savedPaths.filter((path: string) => {
        // Remove Home path if it exists in the saved config
        if (path === '/') {
          console.log('MobileBottomNavigation: Removing Home path from navigation');
          return false;
        }
        
        // Remove special case for Communications path
        // If it's the communications path, automatically convert it to pipeline path
        if (path === '/communications') {
          console.log('MobileBottomNavigation: Converting old Communications path to Pipeline path');
          // Return false to remove it from the current paths
          return false;
        }
        
        // Normal permission check for other paths
        // Tasks path - check if disabled in settings and has permission
        if (path === '/tasks') {
          const allowed = !isPageDisabled('tasks') && canAccess.tasks;
          console.log('MobileBottomNavigation: Checking if tasks path is allowed:', allowed);
          return allowed;
        }
        // If it's the automations path, always allow access (permissions removed)
        if (path === '/automations') {
          const allowed = true;
          console.log('MobileBottomNavigation: Checking if automations path is allowed:', allowed);
          return allowed;
        }
        // For all other paths, keep them
        return true;
      });
      
      // If permissions changed and we filtered out paths, update stored config
      if (filteredSavedPaths.length !== savedPaths.length) {
        console.log('MobileBottomNavigation: Updating config due to permission changes', {
          before: savedPaths,
          after: filteredSavedPaths
        });
        
        // Save updated config to database (only if dependencies are ready)
        if (supabase && user?.id) {
          saveNavConfigToDatabase(filteredSavedPaths);
        } else {
          console.log('MobileBottomNavigation: Skipping save to database - dependencies not ready');
        }
      }
      
      // Reconstruct navigation items from saved paths - ensure we only have valid items (non-undefined)
      const savedItems: NavigationItem[] = filteredSavedPaths
        .map((path: string) => allItems.find(item => item.path === path))
        .filter((item): item is NavigationItem => item !== undefined);
      
      if (savedItems.length > 0) {
        setNavigationItems(savedItems.slice(0, 4)); // Max 4 items
        
        // Set available items (those not in the current nav)
        setAvailableNavItems(
          allItems.filter(item => !savedItems.some(saved => saved.path === item.path))
        );
        
        // Notify parent about current navigation items
        if (onNavItemsChange) {
          onNavItemsChange(filteredSavedPaths);
        }
      } else {
        // Default if no valid items found - pick first 4 available items
        setNavigationItems(allItems.slice(0, 4));
        setAvailableNavItems(allItems.slice(4));
        
        // Notify parent about items
        if (onNavItemsChange) {
          onNavItemsChange(allItems.slice(0, 4).map(item => item.path));
        }
      }
    };
    
    // Load configuration
    loadConfiguration();
    
  }, [hasCalendar, hasBookings, proposalsAccess, hasPayments, hasCustomers, hasMessages, 
      onNavItemsChange, getAllNavigationItems, user?.id, supabase]);

  // Handle removing a navigation item
  const handleRemoveNavItem = (index: number) => {
    const removedItem = navigationItems[index];
    const newItems = [...navigationItems];
    newItems.splice(index, 1);
    
    setNavigationItems(newItems);
    setAvailableNavItems([...availableNavItems, removedItem]);
  };

  // Handle adding a navigation item
  const handleAddNavItem = (item: NavigationItem) => {
    if (navigationItems.length >= 4) return; // Max 4 items
    
    setNavigationItems([...navigationItems, item]);
    setAvailableNavItems(availableNavItems.filter(navItem => navItem.path !== item.path));
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    console.log('Toggle edit mode clicked. Current state:', isEditMode);
    if (isEditMode) {
      // Exiting edit mode, save the configuration
      saveNavConfig();
      console.log('Exiting edit mode and saving navigation config');
    } else {
      console.log('Entering edit mode');
    }
    setIsEditMode(!isEditMode);
    setShowMenu(false);
  };

  // Check clock-in status on component mount
  useEffect(() => {
    const checkClockInStatus = async () => {
      if (!selectedBusiness?.id || !user?.id || !supabase) return;

      try {
        // First get the resource ID and compensation type
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('id, compensation_type')
          .eq('auth_user_id', user.id)
          .eq('business_id', selectedBusiness.id)
          .single();

        if (resourceError) throw resourceError;
        if (!resourceData) return;

        setResourceId(resourceData.id);
        setCompensationType(resourceData.compensation_type);

        // Check if already clocked in
        const { data, error } = await supabase
          .from('payroll_page')
          .select('id, clock_in, break_start, break_end')
          .eq('resource_id', resourceData.id)
          .eq('business_id', selectedBusiness.id)
          .eq('entry_type', 'timeclock')
          .is('clock_out', null)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setClockedIn(true);
          setCurrentPayrollEntryId(data.id);
          setStartTime(new Date(data.clock_in));
          if (data.break_start && !data.break_end) {
            setOnBreak(true);
            setBreakStartTime(new Date(data.break_start));
          }
        }
      } catch (error) {
        console.error('Error checking clock in status:', error);
      }
    };

    checkClockInStatus();
  }, [selectedBusiness?.id, user?.id]);

  // Timer for elapsed time
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (clockedIn && startTime) {
      intervalId = setInterval(() => {
        const now = new Date();
        let diff = now.getTime() - startTime.getTime();
        
        // Subtract break time if on break
        if (onBreak && breakStartTime) {
          diff = diff - (now.getTime() - breakStartTime.getTime());
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [clockedIn, startTime, onBreak, breakStartTime]);

  // Handle click outside for mobile FAB menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      
      // Check if click is inside FAB container
      const isInsideFab = fabRef.current && fabRef.current.contains(target);
      
      // Check if click is inside menu items area
      const menuItemsContainer = document.querySelector('.fab-menu-items');
      const isInsideMenuItems = menuItemsContainer && menuItemsContainer.contains(target);
      
      // Only close if click is outside both FAB and menu items
      if (!isInsideFab && !isInsideMenuItems) {
        setShowBookingOptions(false);
      }
    }

    if (showBookingOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showBookingOptions]);

  const handleClockIn = async () => {
    if (!selectedBusiness?.id || !user?.id || !supabase || !resourceId) return;

    try {
      const now = new Date();
      // Get resource's details for rate information
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate, compensation_type')
        .eq('id', resourceId)
        .single();

      if (resourceError) throw resourceError;
      if (!resourceData) throw new Error('Resource not found');

      // Create the payroll entry
      const { data, error } = await supabase
        .from('payroll_page')
        .insert({
          business_id: selectedBusiness.id,
          resource_id: resourceId,
          clock_in: now.toISOString(),
          entry_type: 'timeclock',
          status: 'active',
          process_status: 'UNPAID',
          compensation_type: resourceData.compensation_type || 'hourly',
          rate_amount: resourceData.hourly_rate || 0,
          total_hours: 0,
          total_minutes: 0,
          total_earnings: 0
        })
        .select()
        .single();

      if (error) throw error;

      setClockedIn(true);
      setCurrentPayrollEntryId(data.id);
      setStartTime(now);
      setShowBookingOptions(false);
    } catch (error) {
      console.error('Error clocking in:', error);
      alert('Failed to clock in. Please try again.');
    }
  };

  const handleClockOut = async () => {
    if (!currentPayrollEntryId || !supabase || !resourceId || !startTime) return;

    // Check for active timers before allowing clock out
    try {
      const { data: activeTimerLogs, error: timerError } = await supabase
        .from('booking_timer_logs')
        .select(`
          booking_id,
          bookings!inner(title)
        `)
        .eq('resource_id', resourceId)
        .eq('is_running', true)
        .limit(1);

      if (timerError) {
        console.error('Error checking for active timers:', timerError);
      } else if (activeTimerLogs && activeTimerLogs.length > 0) {
        // Active timer found, show warning modal
        const bookingTitle = activeTimerLogs[0].bookings?.title || 'Unknown Booking';
        setActiveTimerBookingTitle(bookingTitle);
        setIsActiveTimerWarningOpen(true);
        return; // Don't proceed with clock out
      }
    } catch (error) {
      console.error('Error checking for active timers:', error);
    }

    try {
      // Task constraint check removed - tasks system no longer available

      const now = new Date();

      // Get the current entry
      const { data: currentEntry, error: fetchError } = await supabase
        .from('payroll_page')
        .select('*, resource:resources(hourly_rate)')
        .eq('id', currentPayrollEntryId)
        .single();

      if (fetchError) throw fetchError;
      if (!currentEntry) throw new Error('Entry not found');

      // Calculate total minutes worked
      let totalMs = now.getTime() - startTime.getTime();
      
      // Get all completed breaks
      const { data: breaks, error: breaksError } = await supabase
        .from('payroll_page')
        .select('break_start, break_end')
        .eq('id', currentPayrollEntryId)
        .not('break_start', 'is', null)
        .not('break_end', 'is', null);

      if (breaksError) throw breaksError;

      // Subtract break times
      if (breaks) {
        breaks.forEach(breakPeriod => {
          const breakStart = new Date(breakPeriod.break_start);
          const breakEnd = new Date(breakPeriod.break_end);
          totalMs -= (breakEnd.getTime() - breakStart.getTime());
        });
      }

      // Calculate hours and earnings
      const totalMinutes = Math.round(totalMs / (1000 * 60));
      const hours = totalMinutes / 60;
      const hourlyRate = currentEntry.resource?.hourly_rate || 0;
      const totalEarnings = hours * hourlyRate;

      // Update the entry
      const { error: updateError } = await supabase
        .from('payroll_page')
        .update({
          clock_out: now.toISOString(),
          total_minutes: totalMinutes,
          total_hours: hours,
          total_earnings: totalEarnings,
          status: 'completed'
        })
        .eq('id', currentPayrollEntryId)
        .eq('resource_id', resourceId);

      if (updateError) throw updateError;

      setClockedIn(false);
      setOnBreak(false);
      setCurrentPayrollEntryId(null);
      setStartTime(null);
      setBreakStartTime(null);
      setShowBookingOptions(false);
    } catch (error) {
      console.error('Error clocking out:', error);
      alert('Failed to clock out. Please try again.');
    }
  };

  const handleBreak = async () => {
    if (!currentPayrollEntryId || !supabase || !resourceId) return;

    try {
      const now = new Date();
      if (!onBreak) {
        // Start break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_start: now.toISOString()
          })
          .eq('id', currentPayrollEntryId)
          .eq('resource_id', resourceId);

        if (error) throw error;
        setOnBreak(true);
        setBreakStartTime(now);
      } else {
        // End break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_end: now.toISOString()
          })
          .eq('id', currentPayrollEntryId)
          .eq('resource_id', resourceId);

        if (error) throw error;
        setOnBreak(false);
        setBreakStartTime(null);
      }
      setShowBookingOptions(false);
    } catch (error) {
      console.error('Error handling break:', error);
      alert('Failed to update break status. Please try again.');
    }
  };
  
  // Modified save nav config to only save to database
  const saveNavConfig = () => {
    try {
      const paths = navigationItems.map(item => item.path);
      
      // Save to database only (check dependencies first)
      if (supabase && user?.id) {
        saveNavConfigToDatabase(paths);
      } else {
        console.log('MobileBottomNavigation: Skipping save to database - dependencies not ready');
      }
      
      // Notify parent about navigation changes
      if (onNavItemsChange) {
        onNavItemsChange(paths);
      }
    } catch (error) {
      console.error('Error saving navigation config:', error);
    }
  };
  
  // Save navigation configuration to database
  const saveNavConfigToDatabase = async (paths: string[]) => {
    if (!supabase || !user?.id) {
      console.log('Cannot save nav config to database: Missing supabase client or user ID');
      return;
    }
    
    try {
      console.log('Saving navigation config to database for user:', user.id);
      
      
      // Check if a configuration already exists for this user
      const { data: existingConfig, error: checkError } = await supabase
        .from('user_preferences')
        .select('id')
        .eq('user_id', user.id)
        .eq('preference_type', 'mobile_navigation')
        .maybeSingle();
        
      if (checkError) {
        console.error('Error checking existing navigation config:', checkError);
        return;
      }
      
      if (existingConfig) {
        // Update existing configuration
        const { error: updateError } = await supabase
          .from('user_preferences')
          .update({
            preference_value: paths,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingConfig.id);
          
        if (updateError) {
          console.error('Error updating navigation config in database:', updateError);
        } else {
          console.log('Navigation config updated in database successfully');
        }
      } else {
        // Create new configuration
        const { error: insertError } = await supabase
          .from('user_preferences')
          .insert({
            user_id: user.id,
            preference_type: 'mobile_navigation',
            preference_value: paths,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          
        if (insertError) {
          console.error('Error inserting navigation config to database:', insertError);
        } else {
          console.log('Navigation config saved to database successfully');
        }
      }
    } catch (error) {
      console.error('Error saving navigation config to database:', error);
    }
  };

  return (
    <div className={`mobile-nav-container ${isPWA ? 'pwa-mode' : ''}`}>
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      
      {/* Return nothing visible on first render to avoid any flashing */}
      {firstRender ? (
        <div className="h-14"></div> 
      ) : (
        <>
          {/* Available items menu when in edit mode */}
          {isEditMode && showMenu && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={() => setShowMenu(false)}>
              <div 
                className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-xl p-5 w-11/12 max-w-md max-h-[65vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
                style={{ zIndex: 75 }}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Add Navigation Items</h3>
                  <button 
                    onClick={() => setShowMenu(false)} 
                    className="bg-gray-100 text-gray-500 rounded-full p-1.5 hover:bg-gray-200 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {availableNavItems.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-gray-500 text-sm">All available items have been added</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {availableNavItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleAddNavItem(item)}
                        disabled={navigationItems.length >= 4}
                        className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-150 ${
                          navigationItems.length >= 4 
                            ? 'bg-gray-100 text-gray-400' 
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:scale-105 active:scale-95'
                        }`}
                      >
                        <div className="bg-white p-2 rounded-full shadow-sm mb-2">
                          {item.icon}
                        </div>
                        <span className="text-xs font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => setShowMenu(false)}
                    className="w-full py-2.5 bg-blue-500 text-white font-medium rounded-xl hover:bg-blue-600 active:bg-blue-700 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* FAB Actions menu removed - now handled by unified FloatingActionButton */}
          
          {/* Mobile-specific FAB removed - now using unified FloatingActionButton */}
        </>
      )}

      {/* Active Timer Warning Modal */}
      <ActiveTimerWarningModal
        isOpen={isActiveTimerWarningOpen}
        onClose={() => setIsActiveTimerWarningOpen(false)}
        bookingTitle={activeTimerBookingTitle}
      />
    </div>
  );
} 
